import {forkJoin, noop, Observable, of, Subscription} from 'rxjs';
import {finalize, mergeMap, map} from 'rxjs/operators';
import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ArrayUtils, CollectionUtils} from '@shared/util/collections/collection-utils';
import {Metadata} from '@app/graphql-typings/graphql';
import {Sensor, Template, TemplateId} from '@app/layout/rules/template-editor/api/template';
import {MetadataService} from '@shared/services/api/metadata.service';
import {TemplatesService} from '@app/layout/rules/template-editor/services/templates.service';
import {I18nMessage} from '@app/shared/api/model/i18n';
import {AppConstants} from '@shared/util/app-constants';

type TemplateSelectValue = TemplateId | Template | TemplateId[] | Template[] | null;

@Component({
  selector: 'app-template-select',
  templateUrl: './template-select.component.html',
  styleUrls: ['./template-select.component.scss'],
  providers: [{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TemplateSelectComponent), multi: true}],
  standalone: false
})
export class TemplateSelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

  @Input() appendTo = AppConstants.DROPDOWN_HOLDER_SELECTOR;
  @Input() bindValue: string;
  @Input() placeholder = 'Template';
  @Input() isReadOnly = false;
  @Input() templates: Template[];
  @Input() multiSelect = false;
  @Input() tooltipById: Map<TemplateId, I18nMessage> | null;
  @Input() loading = false;
  @Input() showItemsWhileLoading = false;
  @Input() selectedTemplatesDisplayMax: number;
  @Input() clearable = true;
  @Input() showViewTemplateLink = false;
  @Output() templatesLoaded = new EventEmitter<Template[]>();
  @Output() openEvent = new EventEmitter();
  @Output() closeEvent = new EventEmitter();

  private readonly TEMPLATE_ID_KEY = 'template_id';
  readonly control = new FormControl<TemplateSelectValue>(null);
  readonly dropdownWidth = '30rem';

  private onTouched: any = () => {};
  private onChange: (value: TemplateSelectValue) => void = noop;
  private fetchTemplatesSubscription: Subscription;


  constructor(private metadataService: MetadataService, private templateService: TemplatesService) {}

  ngOnInit(): void {
    this.control.valueChanges.subscribe(value => this.onChange(value));
    this.fetchAndEmitTemplates(false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.bindValue) {
      if (this.bindValue != null && this.bindValue !== this.TEMPLATE_ID_KEY) {
        throw new Error('bindValue must be null or ' + this.TEMPLATE_ID_KEY);
      }
    }
    if (changes.isReadOnly || changes.bindValue || changes.templates){
      this.fetchAndEmitTemplates(!!changes.templates);
    }

    if (changes.showViewTemplateLink || changes.multiSelect) {
      if (this.showViewTemplateLink && this.multiSelect) {
        throw new Error('showViewTemplateLink cannot be true when multiSelect is true. View Template link is only supported for single template selection.');
      }
    }
  }

  ngOnDestroy(): void {
    if (this.fetchTemplatesSubscription != null) {
      this.fetchTemplatesSubscription.unsubscribe();
      this.fetchTemplatesSubscription = null;
    }
  }

  private fetchAndEmitTemplates(isChange: boolean): void {
    if (this.fetchTemplatesSubscription == null && (!this.isReadOnly || this.bindValue != null)) {
      if (this.templates) {
        if (isChange) {
          this.templatesLoaded.emit(this.templates);
        }
      } else {
        this.fetchTemplatesSubscription = this.fetchTemplates()
          .pipe(finalize(() => this.fetchTemplatesSubscription = null))
          .subscribe((templates: Template[]) => {
            this.templates = templates;
            this.templatesLoaded.emit(this.templates);
          });
      }
    }
  }

  registerOnChange(fn: (value: TemplateSelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({emitEvent: false});
    } else {
      this.control.enable({emitEvent: false});
    }
  }

  writeValue(value: TemplateSelectValue): void {
    let valueChanged: boolean;
    if (this.multiSelect) {
      const arr1: TemplateId[] = (<Template[] | TemplateId[]>this.control.value ?? [])?.map(this.getId);
      const arr2: TemplateId[] = (<Template[] | TemplateId[]>value ?? [])?.map(this.getId);
      valueChanged = !ArrayUtils.containSameItems(arr1, arr2);
    } else {
      valueChanged = this.getId(<Template | TemplateId>this.control.value) !== this.getId(<Template | TemplateId>value);
    }

    if (valueChanged) {
      this.control.setValue(value, { emitEvent: false });
    }
  }

  // Always returns a TemplateId for the given item, whether it's a Template or TemplateId.
  private getId(item: Template | TemplateId): TemplateId {
    return item != null && typeof item === 'object' && this.TEMPLATE_ID_KEY in item
      ? item.template_id
      : (<TemplateId>item ?? null);
  }

  getSingleTemplateId(): TemplateId | null {
    const value = this.control.value;
    return (value != null && !Array.isArray(value)) ? this.getId(value) : null;
  }

  onBlur(): void {
    this.onTouched();
  }

  onOpen(): void {
    this.openEvent.emit();
  }

  onClose(): void {
    this.closeEvent.emit();
  }

  searchTemplate = (term: string, template: Template) => {
    const searchTerm = term?.toLocaleLowerCase();
    return template && (template.template_name?.toLocaleLowerCase()?.includes(searchTerm)
      || template.properties?.rule_name?.toLocaleLowerCase()?.includes(searchTerm));
  };

  private fetchTemplates(): Observable<Template[]> {
    this.loading = true;
    return this.templateService.searchAllTemplates({}, {getSensors: true, getProperties: true}).pipe(
      finalize(() => this.loading = false),
      mergeMap((templates: Template[]) => {
        const metadataIds = (templates ?? []).reduce((acc, template) => {
          return template.sensors.reduce((metadataIds, sensor) => metadataIds.add(sensor.metadata_id), acc);
        }, new Set<number>());
        return forkJoin([of(templates ?? []), metadataIds?.size ? this.metadataService.getMetadataByIds(Array.from(metadataIds)) : []]);
      }),
      map(([templates, metadata]: [Template[], Metadata[]]) => {
        const metadataById = CollectionUtils.indexBy(metadata, 'metadata_id');
        templates.forEach(template => {
          template['sensorMetadata'] = Array.from(template.sensors.reduce((metadataNames, sensor: Sensor) => {
            const currentMetadata = metadataById.get(sensor.metadata_id);
            if (currentMetadata) {
              metadataNames.add(currentMetadata.name);
            }
            return metadataNames;
          }, new Set<string>()));
        });

        return templates.sort(this.sortTemplatesByName);
      })
    );
  }

  private sortTemplatesByName(a: Template, b: Template): number {
    if (a.template_name < b.template_name) {
      return -1;
    }
    if (a.template_name > b.template_name) {
      return 1;
    }
    return 0;
  };
}
